// Player's Secret Corner — embeds the vendored strokes_ganied frontend in an isolated
// iframe served from the API's own origin (not this app's origin), and hands it a
// short-lived corner token over postMessage. Route-level gating (ProtectedRoute
// requireSecretCorner) is discoverability only; the real boundary is backend-enforced via
// get_corner_principal. See imp_mds/PLAYERS_SECRET_CORNER_PLAN.md §§3-5.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mintCornerToken, secretCornerAppUrl } from '../services/secretCornerService';

const CORNER_URL = secretCornerAppUrl();
const CORNER_ORIGIN = new URL(CORNER_URL).origin;

export default function SecretCorner() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const sendToken = async () => {
    try {
      const token = await mintCornerToken();
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'corner:token', token },
        CORNER_ORIGIN
      );
    } catch (e) {
      setError('Failed to mint corner session — Secret Corner access required.');
    }
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== CORNER_ORIGIN) return;
      if (event.data?.type === 'corner:ready') {
        sendToken();
      } else if (event.data?.type === 'corner:exit') {
        // The iframe holds only a 15-min corner token, not a session — leaving the
        // Corner is a plain navigation back to the host app, not a logout. Real
        // logout stays in the main app header. See working order §S3 item 3.
        navigate('/');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Exit bar lives in the parent frame — the iframe can't navigate its host. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 16px',
          background: '#0f172a',
          color: '#e2e8f0',
          fontSize: 13,
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 600, letterSpacing: '0.02em' }}>Player's Secret Corner</span>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            background: 'transparent',
            border: '1px solid #334155',
            color: '#e2e8f0',
            borderRadius: 6,
            padding: '4px 12px',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Exit to dialledin.golf
        </button>
      </div>
      {error && (
        <div style={{ padding: '8px 16px', background: '#fee2e2', color: '#991b1b' }}>{error}</div>
      )}
      {/* allow-downloads is required by the shareable scorecard PNG — a sandboxed frame's
          programmatic download link is silently dropped without it. */}
      <iframe
        ref={iframeRef}
        src={CORNER_URL}
        title="Player's Secret Corner"
        sandbox="allow-scripts allow-forms allow-same-origin allow-downloads"
        style={{ flex: 1, border: 'none' }}
      />
    </div>
  );
}
